import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../services/api";

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading"); // loading, success, failed
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("failed");
        return;
      }

      try {
        const response = await api.get(`/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === "paid") {
          setStatus("success");
          setPaymentData(data);
          return;
        } else if (data.status === "expired") {
          setStatus("failed");
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Error checking payment status:", error);
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  return (
    <div
      data-testid="checkout-success-page"
      className="min-h-screen bg-[var(--dvx-bg)] pt-24 flex items-center justify-center transition-colors duration-300"
    >
      <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {status === "loading" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Loader2 className="w-16 h-16 text-[var(--dvx-primary)] mx-auto animate-spin" />
            <h1 className="mt-6 font-oswald text-3xl font-bold text-[var(--dvx-text)] uppercase">
              Processing Payment
            </h1>
            <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope">
              Please wait while we confirm your payment...
            </p>
          </motion.div>
        )}

        {status === "success" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="w-20 h-20 mx-auto bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="mt-6 font-oswald text-4xl font-bold text-[var(--dvx-text)] uppercase">
              Payment Successful!
            </h1>
            <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg">
              Welcome to DVX! Your journey to elite performance starts now.
            </p>

            {paymentData && (
              <div className="mt-8 bg-[var(--dvx-surface)] border border-[var(--dvx-border)] p-6 text-left transition-colors duration-300">
                <h3 className="font-oswald text-lg font-bold text-[var(--dvx-text)] uppercase mb-4">
                  Order Summary
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-[var(--dvx-text-muted)]">Plan</span>
                    <span className="text-[var(--dvx-text)] font-oswald">
                      {paymentData.metadata?.plan_name || "DVX Plan"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--dvx-text-muted)]">Billing</span>
                    <span className="text-[var(--dvx-text)] font-oswald capitalize">
                      {paymentData.metadata?.billing_cycle || "Monthly"}
                    </span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-[var(--dvx-border)]">
                    <span className="text-[var(--dvx-text-muted)]">Total</span>
                    <span className="text-[var(--dvx-primary)] font-oswald text-xl font-bold">
                      ${(paymentData.amount_total / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 space-y-4">
              <p className="text-[var(--dvx-text-muted)] font-manrope text-sm">
                A confirmation email has been sent to your inbox with your login
                details.
              </p>
              <Link to="/">
                <Button
                  data-testid="go-home-btn"
                  className="rounded-none px-8 py-4 bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white font-oswald font-bold uppercase tracking-widest hover:scale-105 transition-transform"
                >
                  Start Training
                </Button>
              </Link>
            </div>
          </motion.div>
        )}

        {status === "failed" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="w-20 h-20 mx-auto bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="mt-6 font-oswald text-4xl font-bold text-[var(--dvx-text)] uppercase">
              Payment Failed
            </h1>
            <p className="mt-4 text-[var(--dvx-text-muted)] font-manrope text-lg">
              Something went wrong with your payment. Please try again or contact
              support.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing">
                <Button
                  data-testid="try-again-btn"
                  className="rounded-none px-8 py-4 bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white font-oswald font-bold uppercase tracking-widest"
                >
                  Try Again
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="rounded-none px-8 py-4 border-[var(--dvx-border)] text-[var(--dvx-text)] font-oswald font-bold uppercase tracking-widest hover:bg-[var(--dvx-surface)]"
                >
                  Contact Support
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
